/* eslint-disable i18next/no-literal-string */
import Modal, { Sheets, ModalBody, ModalHeader } from '@ingka/modal';
import ListView, { ListViewItem } from '@ingka/list-view';
import Text from '@ingka/text';
import type { MouseEvent } from 'react';
import { useState } from 'react';
import globe from '@ingka/ssr-icon/paths/globe';
import store from '@ingka/ssr-icon/paths/store';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import { useTranslation } from 'react-i18next';
import {
  useGetPermissionsQuery,
  useGetRetailUnitQuery,
} from '@lotta/self-service/lottery-fe/data-access-state';
import Tooltip from '@ingka/tooltip';
import { LogoutButton } from '../logout-button/LogoutButton';
import { RetailUnitSlider } from './retail-unit-slider/RetailUnitSlider';
import { LanguageSlider } from './language-slider/LanguageSlider';
import styles from './ProfileSlider.module.scss';

export type ProfileSliderProps = {
  modalVisible: boolean;
  handleCloseBtn: (event: MouseEvent<HTMLButtonElement>) => void;
};

/**
 * ProfileSlider
 *
 * Note that this component is rendered outside of the AccessVerification
 * component, and therefor need to be careful about permissions.
 */
export const ProfileSlider: React.FC<ProfileSliderProps> = (props) => {
  const { modalVisible, handleCloseBtn } = props;
  const { t } = useTranslation();
  const { user } = useAuth0();
  const [languageSelectionVisible, setLanguageSelectionVisible] = useState(false);
  const [retailUnitSelectionVisible, setRetailUnitSelectionVisible] = useState(false);
  const { language, retailUnit } = useLocale();
  const { data, isLoading } = useGetPermissionsQuery({});
  const hasPermissionForCurrentRetailUnit = !!data?.permissions.find(
    (p) => p.retailUnitId === retailUnit
  );
  const { data: retailUnitData, isLoading: isRetailUnitLoading } = useGetRetailUnitQuery(
    { retailUnit },
    { skip: !hasPermissionForCurrentRetailUnit }
  );

  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    handleCloseBtn(event);
    setLanguageSelectionVisible(false);
    setRetailUnitSelectionVisible(false);
  };

  const permission = () => {
    if (data && !isLoading) {
      return data.permissions.find((p) => p.retailUnitId === retailUnit);
    }
    return undefined;
  };

  const hasMultipleRetailUnits = () => {
    if (data && !isLoading) {
      return data.permissions.length > 1;
    } else {
      return false;
    }
  };

  const hasMultipleLanguages = () => {
    if (retailUnitData && !isRetailUnitLoading) {
      return retailUnitData.retailUnit.selfService.languages.length > 1;
    }
    return false;
  };

  const retailUnitListItem = (
    <ListViewItem
      disabled={!hasMultipleRetailUnits()}
      onClick={() => {
        setRetailUnitSelectionVisible(true);
      }}
      inset
      title={`${t('header-modal-retail-unit-action')}`}
      emphasised
      description={retailUnit.toUpperCase()}
      leadingIcon={store}
      control="navigational"
      controlIcon="chevronRight"
      data-testid="retailActionListItem"
    />
  );

  const languageListItem = (
    <ListViewItem
      disabled={!hasMultipleLanguages()}
      onClick={() => {
        setLanguageSelectionVisible(true);
      }}
      inset
      title={`${t('header-modal-language-action')}`}
      emphasised
      description={language.toUpperCase()}
      leadingIcon={globe}
      control="navigational"
      controlIcon="chevronRight"
      data-testid="languageActionListItem"
    />
  );

  return (
    <Modal visible={modalVisible} handleCloseBtn={handleCloseBtn}>
      <Sheets footer={null} header={<ModalHeader />}>
        <ModalBody>
          <div className={styles['user-div']}>
            <Text headingSize="m">Hej {user?.given_name}</Text>
            <LogoutButton />
          </div>
          <Text bodySize="m">{user?.email}</Text>
          <Text bodySize="m">{permission()?.permission}</Text>
          <div className={styles['user-info']} />
          <hr className="demo-divider__hr-horizontal" />
          <ListView id="userActions" className={styles['list-view']}>
            {!hasMultipleRetailUnits() ? (
              <Tooltip
                interactiveCustomTrigger
                className={styles['list-view-tooltip']}
                tooltipText={t('header-modal-retail-unit-tooltip')}
              >
                {retailUnitListItem}
              </Tooltip>
            ) : (
              retailUnitListItem
            )}
            {!hasMultipleLanguages() ? (
              <Tooltip
                interactiveCustomTrigger
                className={styles['list-view-tooltip']}
                tooltipText={t('header-modal-language-tooltip')}
              >
                {languageListItem}
              </Tooltip>
            ) : (
              languageListItem
            )}
          </ListView>
          <RetailUnitSlider
            modalVisible={retailUnitSelectionVisible}
            handleCloseBtn={handleClose}
            handleBackBtn={() => setRetailUnitSelectionVisible(false)}
          />
          <LanguageSlider
            modalVisible={languageSelectionVisible}
            handleCloseBtn={handleClose}
            handleBackBtn={() => setLanguageSelectionVisible(false)}
          />
        </ModalBody>
      </Sheets>
    </Modal>
  );
};
